import { useEffect, useState } from 'react';
import { Transaction } from '../types/transaction.types';
import { findTicket } from '../services/TicketsService';

/** Los ids de factura son correlativos: con un solo digito ya hay que buscar. */
const MIN_QUERY_LENGTH = 1;
const DEBOUNCE_MS = 300;

/**
 * Buscador de tickets de la seccion de devoluciones. Manda tal cual lo escrito
 * a findTicket, que lo resuelve como id interno o como numero de ticket
 * (T1-25). NO pasa por books_list, que en devoluciones no pinta nada.
 *
 * `enabled` lo apaga fuera de devoluciones para que el mismo input no dispare
 * las dos busquedas a la vez.
 */
export const useInvoiceSearch = (query: string, enabled: boolean) => {
    const [invoice, setInvoice] = useState<Transaction | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const term = query.trim();

        if (!enabled || term.length < MIN_QUERY_LENGTH) {
            setInvoice(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        // Mismo patron que useBookSearch: debounce por tecla y `cancelled` para
        // que una respuesta lenta no pise a la de una query posterior.
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        const timer = setTimeout(() => {
            findTicket(term)
                .then((found) => {
                    if (cancelled) return;
                    setInvoice(found || null);
                    setIsLoading(false);
                })
                .catch((err) => {
                    if (cancelled) return;
                    setInvoice(null);
                    setError(err?.message || 'No se pudo consultar el ticket.');
                    setIsLoading(false);
                });
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, enabled]);

    return { invoice, isLoading, error };
};
